import { Button } from '@/components/ui/button';
import { Edit, Send, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';

interface ClientActionsProps {
  client: {
    id: number;
    email?: string;
  };
  onClientDeleted?: () => void;
}

export const ClientActions = ({ client, onClientDeleted }: ClientActionsProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleDeleteClient = async () => {
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', client.id);

      if (error) throw error;

      toast({
        title: "Client Deleted",
        description: "The client has been successfully deleted.",
      });

      if (onClientDeleted) {
        onClientDeleted();
      }

      navigate('/admin');
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete client: " + error.message,
      });
    }
  };

  const handleSendNotification = async () => {
    if (!subject.trim() || !message.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in both subject and message fields.",
      });
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch('/functions/v1/send-client-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          clientId: client.id,
          subject,
          message,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send notification');
      }

      toast({
        title: "Notification Sent",
        description: `Email has been sent to ${client.email}`,
      });

      setSubject('');
      setMessage('');
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send notification: " + error.message,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate(`/admin/clients/${client.id}/edit`)}
        className="flex items-center gap-2 text-black dark:text-white hover:text-black dark:hover:text-white"
      >
        <Edit className="h-4 w-4" />
        Edit Client
      </Button>
      <Dialog>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 text-black dark:text-white hover:text-black dark:hover:text-white"
            disabled={!client.email}
          >
            <Send className="h-4 w-4" />
            Send Email
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Email to Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-400">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-400">Message</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your message"
                rows={4}
              />
            </div>
            <Button 
              onClick={handleSendNotification}
              disabled={isSending || !subject.trim() || !message.trim()}
              className="w-full"
            >
              {isSending ? 'Sending...' : 'Send Email'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Button
        variant="destructive"
        size="sm"
        onClick={handleDeleteClient}
        className="flex items-center gap-2"
      >
        <Trash2 className="h-4 w-4" />
        Delete Client
      </Button>
    </div>
  );
};